// Fleet status — dashboard tiles + OLT list (+ per-OLT ONU snapshot) +
// router list (read-only), plus OLT- and router-inventory CRUD (admin).

import type { ApiClient } from "./client.js";
import type {
  DashboardPollerResponse,
  DashboardTilesResponse,
  OltDeleteResult,
  OltModelsResponse,
  OltOnusResponse,
  OltPanelResponse,
  OltSaveRequest,
  OltSaveResult,
  OltsResponse,
  RouterDeleteResult,
  RouterSaveRequest,
  RouterSaveResult,
  RoutersResponse,
} from "./types.js";

export function makeFleetApi(client: ApiClient) {
  return {
    /** Home-page overview counts (routers/OLTs online, sessions, issues). */
    tiles(): Promise<DashboardTilesResponse> {
      return client.get<DashboardTilesResponse>("/v1/api/dashboard/tiles");
    },
    /** Navbar daemon-health widget — poller heartbeat + per-fleet counts. */
    poller(): Promise<DashboardPollerResponse> {
      return client.get<DashboardPollerResponse>("/v1/api/dashboard/poller");
    },
    /** OLT status list + fleet aggregates. */
    olts(): Promise<OltsResponse> {
      return client.get<OltsResponse>("/v1/api/olts");
    },
    /** Cached ONU snapshot for one OLT — port/index/name/subscriber/online
     *  (+ optical fields where the poller has them). Powers the OLT "View
     *  details" dialog. `onus` is [] when the OLT hasn't been polled
     *  recently / has no ONUs. */
    oltOnus(name: string): Promise<OltOnusResponse> {
      return client.get<OltOnusResponse>("/v1/api/olts/onus", { olt: name });
    },
    /** Front-panel snapshot for one OLT — PON-port state (+ ONU counts +
     *  SFP optical), GE/uplink-port state, PSU states, chassis health.
     *  `panel` is null when the driver doesn't produce one yet (e.g. GPON
     *  for now — VSOL EPON only). */
    oltPanel(name: string): Promise<OltPanelResponse> {
      return client.get<OltPanelResponse>("/v1/api/olts/panel", { olt: name });
    },
    /** Registered OLT driver models — for the Add/Edit OLT modal dropdown.
     *  Reads the live driver registry, so a new brand needs no UI edit. */
    oltModels(): Promise<OltModelsResponse> {
      return client.get<OltModelsResponse>("/v1/api/cmd/olts/available_models");
    },
    /** Add (`id: 0`) or edit an OLT — admin only. Empty/omitted `ssh_pass`
     *  keeps the stored Telnet password on update; the Go side publishes
     *  `noc:olt_reload` so the poller re-reads its device list. 409 on a
     *  duplicate name or a duplicate IP. */
    saveOlt(req: OltSaveRequest): Promise<OltSaveResult> {
      const b: Record<string, unknown> = {
        id: req.id,
        name: req.name,
        ip: req.ip,
        snmp_community: req.snmp_community,
        snmp_version: req.snmp_version,
        model: req.model,
        ssh_user: req.ssh_user,
        ssh_port: req.ssh_port,
        enabled: req.enabled,
      };
      if (req.ssh_pass) b.ssh_pass = req.ssh_pass;
      return client.post<OltSaveResult>("/v1/api/olts/save", b);
    },
    /** Delete an OLT — admin only. */
    removeOlt(id: number): Promise<OltDeleteResult> {
      return client.post<OltDeleteResult>("/v1/api/olts/delete", { id });
    },
    /** Mikrotik router status list + fleet aggregates. */
    routers(): Promise<RoutersResponse> {
      return client.get<RoutersResponse>("/v1/api/routers");
    },
    /** Add (`id: 0`) or edit a router — admin only. Empty/omitted `password`
     *  keeps the stored one on update (required on create); the Go side
     *  signals the live poller to re-read its device list. 409 on a
     *  duplicate name. */
    saveRouter(req: RouterSaveRequest): Promise<RouterSaveResult> {
      const b: Record<string, unknown> = {
        id: req.id,
        name: req.name,
        ip: req.ip,
        port: req.port,
        username: req.username,
        use_ssl: req.use_ssl,
        enabled: req.enabled,
      };
      if (req.password) b.password = req.password;
      return client.post<RouterSaveResult>("/v1/api/routers/save", b);
    },
    /** Delete a router — admin only. */
    removeRouter(id: number): Promise<RouterDeleteResult> {
      return client.post<RouterDeleteResult>("/v1/api/routers/delete", { id });
    },
  };
}

export type FleetApi = ReturnType<typeof makeFleetApi>;
